@file:OptIn(kotlin.concurrent.atomics.ExperimentalAtomicApi::class)

package com.dotpx.ambient

import kotlin.concurrent.atomics.AtomicLong
import kotlin.concurrent.atomics.AtomicLongArray

/** Primitive status mailbox; [snapshot] allocates only on its UI caller. */
internal class PackedMorphStatus(
    activeScene: AmbienceScene,
    activeSeed: ULong,
    activeMatchesPresetCombination: Boolean,
) {
    private val revision = AtomicLong(0L)
    private val fields = AtomicLongArray(FIELD_COUNT)

    init {
        publish(
            isMorphing = false,
            activeScene = activeScene,
            activeSeed = activeSeed,
            activeMatchesPresetCombination = activeMatchesPresetCombination,
            targetScene = null,
            targetSeed = 0uL,
            remainingFrames = 0L,
            consumedCommandGeneration = 0L,
            completedCommandGeneration = 0L,
        )
    }

    fun publish(
        isMorphing: Boolean,
        activeScene: AmbienceScene,
        activeSeed: ULong,
        activeMatchesPresetCombination: Boolean,
        targetScene: AmbienceScene?,
        targetSeed: ULong,
        remainingFrames: Long,
        consumedCommandGeneration: Long,
        completedCommandGeneration: Long,
    ) {
        revision.fetchAndAdd(1L)
        fields.storeAt(MODE, if (isMorphing) 1L else 0L)
        fields.storeAt(ACTIVE_SCENE, activeScene.ordinal.toLong())
        fields.storeAt(ACTIVE_SEED, activeSeed.toLong())
        fields.storeAt(ACTIVE_MATCHES_PRESET, if (activeMatchesPresetCombination) 1L else 0L)
        fields.storeAt(TARGET_SCENE, targetScene?.ordinal?.toLong() ?: NO_SCENE)
        fields.storeAt(TARGET_SEED, targetSeed.toLong())
        fields.storeAt(REMAINING_FRAMES, remainingFrames)
        fields.storeAt(CONSUMED_GENERATION, consumedCommandGeneration)
        fields.storeAt(COMPLETED_GENERATION, completedCommandGeneration)
        revision.fetchAndAdd(1L)
    }

    fun snapshot(): AmbienceMorphStatus {
        var mode: Long
        var activeSceneRaw: Long
        var activeSeedRaw: Long
        var activeMatchesPreset: Long
        var targetSceneRaw: Long
        var targetSeedRaw: Long
        var remainingFrames: Long
        var consumedGeneration: Long
        var completedGeneration: Long
        while (true) {
            val revisionBefore = revision.load()
            if (revisionBefore and 1L != 0L) continue
            mode = fields.loadAt(MODE)
            activeSceneRaw = fields.loadAt(ACTIVE_SCENE)
            activeSeedRaw = fields.loadAt(ACTIVE_SEED)
            activeMatchesPreset = fields.loadAt(ACTIVE_MATCHES_PRESET)
            targetSceneRaw = fields.loadAt(TARGET_SCENE)
            targetSeedRaw = fields.loadAt(TARGET_SEED)
            remainingFrames = fields.loadAt(REMAINING_FRAMES)
            consumedGeneration = fields.loadAt(CONSUMED_GENERATION)
            completedGeneration = fields.loadAt(COMPLETED_GENERATION)
            if (revisionBefore == revision.load()) break
        }
        val targetScene = if (targetSceneRaw == NO_SCENE) {
            null
        } else {
            AmbienceScene.entries.getOrNull(targetSceneRaw.toInt())
        }
        return AmbienceMorphStatus(
            isMorphing = mode != 0L,
            activeScene = AmbienceScene.entries.getOrElse(activeSceneRaw.toInt()) { AmbienceScene.RainSchool },
            activeSeed = activeSeedRaw.toULong(),
            activeMatchesPresetCombination = activeMatchesPreset != 0L,
            targetScene = targetScene,
            targetSeed = if (targetScene == null) null else targetSeedRaw.toULong(),
            remainingFrames = remainingFrames,
            consumedCommandGeneration = consumedGeneration,
            completedCommandGeneration = completedGeneration,
        )
    }

    private companion object {
        const val MODE = 0
        const val ACTIVE_SCENE = 1
        const val ACTIVE_SEED = 2
        const val ACTIVE_MATCHES_PRESET = 3
        const val TARGET_SCENE = 4
        const val TARGET_SEED = 5
        const val REMAINING_FRAMES = 6
        const val CONSUMED_GENERATION = 7
        const val COMPLETED_GENERATION = 8
        const val FIELD_COUNT = 9
        const val NO_SCENE = -1L
    }
}
